// Runs one server-registered test on this screen: works out the geometry and calibration,
// tells the server the effective config, runs the procedure, streams trials, and asks the
// server to score it.

import { api, type Calibration, type DisplayProfile, type SessionInfo, type TestInfo } from "../api";
import { codeForRel, nominalSrgbLut } from "../lib/display";
import { makeSnapshot } from "../lib/displayInfo";
import { knownScreenDetails, measureRefreshHz } from "../lib/probe";
import { testLook, withStaircaseDefaults, type AcuityConfig, type ContrastConfig, type TestExperiment } from "../lib/protocol";
import { mulberry32, randomSeed } from "../lib/rng";
import { Geometry, LETTER_STROKES, letterArcminToLogmar } from "../lib/units";
import type { ERenderer } from "../render/eRenderer";
import { ComplianceMonitor } from "./compliance";
import { RafPresenter, type ResponseQueue } from "./presenter";
import { AbortedError, drawableCeilingLogCs, runAcuity, runContrast, type RunnerEnv } from "./runner";
import { TrialSink } from "./sink";

export interface StationContext {
  display: DisplayProfile;
  calibration: Calibration | null;
}

export async function loadStation(session: SessionInfo): Promise<StationContext> {
  if (session.display_profile_id === null) {
    throw new Error("This session has no registered display, so letter sizes cannot be computed.");
  }
  const display = await api.display(session.display_profile_id);
  let calibration: Calibration | null = null;
  try {
    calibration = await api.latestCalibration(display.id);
  } catch {
    calibration = null;
  }
  return { display, calibration };
}

/** Geometry of the screen this window is on, using its physical size from the profile. */
export function screenGeometry(display: DisplayProfile, distanceMm: number): { geometry: Geometry; warning: string | null } {
  const dpr = window.devicePixelRatio;
  const wPx = Math.round(window.screen.width * dpr);
  const hPx = Math.round(window.screen.height * dpr);
  let warning: string | null = null;
  if (Math.abs(wPx - display.width_px) > 2) {
    warning = `This screen is running at ${wPx}×${hPx} device pixels but the profile was ` +
      `registered at ${display.width_px}×${display.height_px}. If the OS is scaling, ` +
      `edges are resampled; use a mode where they match.`;
  }
  return { geometry: new Geometry(distanceMm, display.width_mm, wPx, hPx), warning };
}

export function outputBitsFor(calibration: Calibration | null, renderer: ERenderer): number {
  const buffer = renderer.info.float16 ? 10 : 8;
  if (!calibration) return buffer;
  return Math.min(calibration.output_bits, buffer);
}

function effectiveAcuity(cfg: AcuityConfig, g: Geometry): AcuityConfig {
  const drawableMin = letterArcminToLogmar((LETTER_STROKES / g.pxPerDeg) * 60); // 1-px strokes
  const lo = Math.round(Math.max(cfg.min_logmar, drawableMin) * 1e4) / 1e4;
  const hi = Math.round(Math.min(cfg.max_logmar, g.maxLogmar()) * 1e4) / 1e4;
  if (lo >= hi) throw new Error(`At this distance the screen can only show logMAR ${lo.toFixed(2)}–${hi.toFixed(2)}; change the viewing distance.`);
  const clamp = (v: number) => Math.min(Math.max(v, lo), hi);
  return { ...cfg, min_logmar: lo, max_logmar: hi, start_low: clamp(cfg.start_low), start_high: clamp(cfg.start_high) };
}

/** Staircases never ask for a contrast this display cannot draw: the hardest value is the
 *  one-code-step contrast. Stored with the test, with the trial-order seed, so the server
 *  replays the same bounds and the same order. */
function effectiveContrast(cfg: ContrastConfig, env: Parameters<typeof drawableCeilingLogCs>[0]): ContrastConfig {
  if (cfg.method !== "staircases") return cfg;
  const c = withStaircaseDefaults(cfg);
  const ceiling = Math.floor(drawableCeilingLogCs(env) * 1e4) / 1e4;
  // a fresh seed for this test's trial order (kept if a resumed test already has one)
  return { ...c, staircase_max_log_cs: Math.min(c.staircase_max_log_cs, ceiling),
    planner_seed: c.planner_seed ?? randomSeed() };
}

export interface RunOptions {
  session: SessionInfo;
  test: TestInfo;
  station: StationContext;
  renderer: ERenderer;
  responses: ResponseQueue;
  signal: AbortSignal;
  dither?: number;
  /** Home / single-window mode: switching to another app invalidates the test. */
  requireFocus?: boolean;
  /** Home mode: the test must run fullscreen. */
  requireFullscreen?: boolean;
  onProgress?: RunnerEnv["progress"];
  onSink?: (s: { pending: number; error: string | null }) => void;
  onCompliance?: (reason: string) => void;
}

export async function runServerTest(o: RunOptions): Promise<TestInfo> {
  const { session, test, station, renderer } = o;
  const { geometry } = screenGeometry(station.display, session.viewing_distance_mm);
  const lut = station.calibration?.lut_rel ?? nominalSrgbLut();
  const bits = outputBitsFor(station.calibration, renderer);
  // this test's look: its experiment's background, letter colour and contrast mode
  const look = testLook(session.protocol, test as TestInfo & { experiment?: TestExperiment | null });

  let config: AcuityConfig | ContrastConfig;
  let acuityLogmar: number | null = null;
  if (test.kind === "acuity") {
    config = effectiveAcuity(test.config as unknown as AcuityConfig, geometry);
  } else {
    config = effectiveContrast(test.config as unknown as ContrastConfig,
      { lut, outputBits: bits, backgroundRel: look.background_rel, appearance: look.appearance });
    const fresh = await api.session(session.id);
    // the most recent acuity for this eye that counted (a repeat or confirmation run may follow the first)
    const counted = fresh.tests.filter((t) => t.kind === "acuity" && t.eye === test.eye && t.status === "complete"
      && t.result?.valid !== false && typeof t.result?.logmar === "number");
    const lm = counted[counted.length - 1]?.result?.logmar;
    acuityLogmar = typeof lm === "number" ? lm : null;
  }
  const snapshot = makeSnapshot({
    display: station.display, calibration: station.calibration, geometry, stage: renderer.info,
    canvas: { width: renderer.stage.width, height: renderer.stage.height }, outputBits: bits,
    backgroundCode: codeForRel(lut, look.background_rel),
    refreshHz: await measureRefreshHz(60), screenLabel: knownScreenDetails()?.current.label ?? null,
  });
  await api.startTest(test.id, config, snapshot);

  // Our own abort, fired by the operator (outer signal) or by a compliance violation.
  const inner = new AbortController();
  if (o.signal.aborted) inner.abort();
  o.signal.addEventListener("abort", () => inner.abort());
  const monitor = new ComplianceMonitor((e) => {
    o.onCompliance?.(e.type);
    inner.abort();
  }, { requireFocus: o.requireFocus, requireFullscreen: o.requireFullscreen,
    screenDetails: knownScreenDetails()?.target ?? null });

  const sink = new TrialSink((batch) => api.addTrials(test.id, batch), 5, o.onSink);
  const env: RunnerEnv = {
    presenter: new RafPresenter(renderer, o.responses, bits, o.dither ?? 0),
    geometry, lut, outputBits: bits, backgroundRel: look.background_rel, appearance: look.appearance,
    rng: mulberry32(randomSeed()), signal: inner.signal,
    submit: (t) => sink.push(t), progress: o.onProgress,
  };
  let aborted = false;
  try {
    if (test.kind === "acuity") await runAcuity(config as AcuityConfig, env);
    else await runContrast(config as ContrastConfig, acuityLogmar, env);
  } catch (e) {
    if (!(e instanceof AbortedError)) {
      monitor.stop();
      throw e;
    }
    aborted = true;
  }
  const compliance = monitor.stop();
  await sink.flush();
  return api.completeTest(test.id, aborted, compliance);
}
